import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Loader2, AlertTriangle, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, dt } from "@/lib/api";
import { ProjectSelect } from "@/components/common";
import { AmCtx, StatusPill } from "@/components/am/shared";
import Tree from "@/components/am/Tree";
import CampaignForm from "@/components/am/CampaignForm";
import AdSetForm from "@/components/am/AdSetForm";
import AdForm from "@/components/am/AdForm";
import ReviewPanel from "@/components/am/ReviewPanel";
import Insights from "@/components/am/Insights";

function NewCampaign({ opts, projectId, onCreated, prefill }) {
  const [open, setOpen] = useState(false); const [f, setF] = useState({ name: "", objective: "OUTCOME_LEADS", project_id: projectId || "" });
  useEffect(() => { if (prefill) { setF((p) => ({ ...p, ...prefill })); setOpen(true); } }, [prefill]);
  const go = async () => { try { const r = await api.post("/ads-manager/campaigns", { ...f, project_id: f.project_id || null }); setOpen(false); setF({ name: "", objective: "OUTCOME_LEADS", project_id: projectId || "" }); onCreated(r.data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="create-campaign-btn"><Plus className="h-4 w-4 mr-1" />Create campaign</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="am-new-dialog"><DialogHeader><DialogTitle>Create campaign</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">You will set up the campaign, its ad sets and ads (Meta's Campaign → Ad set → Ad structure), then review before anything is sent to Meta.</p>
          <Input placeholder="Campaign name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="am-new-name" />
          <Select value={f.objective} onValueChange={(v) => setF({ ...f, objective: v })}><SelectTrigger data-testid="am-new-objective"><SelectValue /></SelectTrigger>
            <SelectContent>{opts.objectives.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent></Select>
          {!projectId && <ProjectSelect value={f.project_id} onChange={(v) => setF({ ...f, project_id: v })} allowAll testid="am-new-project" className="w-full" />}
          <Button disabled={!f.name.trim()} onClick={go} data-testid="am-new-create">Create draft</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

const STEP_LABELS = [["campaign", "Campaign"], ["adset", "Ad set"], ["ad", "Ad"], ["review", "Review & publish"]];

function Steps({ cur, order, idx, go }) {
  const firstOf = (lvl) => order.find((o) => o.level === lvl);
  const count = (lvl) => order.filter((o) => o.level === lvl).length;
  const pos = (lvl) => order.slice(0, idx + 1).filter((o) => o.level === lvl).length;
  return (
    <div className="flex flex-wrap gap-1 mb-4" data-testid="step-bar">
      {STEP_LABELS.map(([k, l], i) => (
        <button key={k} onClick={() => firstOf(k) && go(firstOf(k))} data-testid={`step-${k}`}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${cur === k ? "border-emerald-500 bg-emerald-500/10 font-semibold" : "border-slate-800 hover:bg-slate-900"}`}>
          {i + 1}. {l}{cur === k && count(k) > 1 ? ` (${pos(k)} of ${count(k)})` : ""}
        </button>))}
    </div>
  );
}

function Readiness({ rd }) {
  if (!rd || rd.ok) return rd?.ok ? <p className="text-xs text-emerald-600 flex gap-1 items-center" data-testid="am-readiness"><CheckCircle2 className="h-4 w-4" />Meta ready · {rd.assets.ad_account_name || rd.assets.ad_account_id} · {rd.assets.page_name}</p> : null;
  return (
    <div className="rounded-xl border border-amber-500/40 p-3 text-xs flex gap-2" data-testid="am-readiness">
      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
      <div className="space-y-0.5"><div className="font-semibold">You can build and save drafts now. Creating on Meta needs:</div>{rd.blockers.map((b, i) => <div key={i}>• {b}</div>)}</div>
    </div>
  );
}

export default function UnifiedBuilder({ projectId }) {
  const [params, setParams] = useSearchParams();
  const [opts, setOpts] = useState(null); const [list, setList] = useState([]); const [tree, setTree] = useState(null); const [sel, setSel] = useState(null);
  const [readiness, setReadiness] = useState(null); const [media, setMedia] = useState([]); const [issues, setIssues] = useState([]); const [tab, setTab] = useState("build");
  const [prefill, setPrefill] = useState(null); const flushRef = useRef(null); const [moving, setMoving] = useState(false);
  const loadList = useCallback(() => api.get("/ads-manager/campaigns", { params: { project_id: projectId || undefined } }).then((r) => setList(r.data)), [projectId]);
  const reloadMedia = useCallback(() => api.get("/ads-manager/media").then((r) => setMedia(r.data)), []);
  const open = useCallback(async (id, t = "build") => {
    try { const r = await api.get(`/ads-manager/campaigns/${id}`); setTree(r.data); setSel({ level: "campaign", id }); setTab(t); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => {
    api.get("/ads-manager/options").then((r) => setOpts(r.data));
    api.get("/ads-manager/readiness").then((r) => setReadiness(r.data)).catch(() => {});
    loadList(); reloadMedia();
  }, [loadList, reloadMedia]);
  useEffect(() => {
    if (params.get("new")) { setPrefill({ objective: params.get("objective") || "OUTCOME_LEADS", project_id: params.get("project") || projectId || "", goal: params.get("goal") || undefined }); setParams({}, { replace: true }); return; }
    const id = params.get("campaign");
    if (!id) return;
    open(id, params.get("tab") || "build");
    setParams({}, { replace: true });
  }, [params, setParams, open, projectId]);
  useEffect(() => { if (tree) api.get(`/ads-manager/campaigns/${tree.campaign.id}/review`).then((r) => setIssues(r.data.issues)).catch(() => {}); }, [tree]);
  useEffect(() => { if (tree) loadList(); }, [tree, loadList]);
  if (!opts) return <Loader2 className="h-5 w-5 animate-spin" />;
  const mediaMap = Object.fromEntries(media.map((m) => [m.id, m]));
  const entity = sel && tree && (sel.level === "campaign" ? tree.campaign : sel.level === "adset" ? tree.adsets.find((a) => a.id === sel.id) : tree.adsets.flatMap((a) => a.ads).find((a) => a.id === sel.id));
  const pick = (s) => { setSel(s); setTab("build"); setTimeout(() => window.innerWidth < 1024 && document.querySelector("[data-testid=am-editor]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const ctx = { opts, tree, setTree, sel, setSel: pick, readiness, media: mediaMap, mediaList: media, reloadMedia, issues, flushRef };
  const order = tree ? [{ level: "campaign", id: tree.campaign.id }, ...tree.adsets.flatMap((a) => [{ level: "adset", id: a.id }, ...a.ads.map((x) => ({ level: "ad", id: x.id }))]), { level: "review" }] : [];
  const idx = tab === "review" ? order.length - 1 : order.findIndex((o) => o.level === sel?.level && o.id === sel?.id);
  const go = (o) => (o.level === "review" ? setTab("review") : pick(o));
  const next = async () => {
    setMoving(true);
    try {
      await flushRef.current?.();
      const r = (await api.get(`/ads-manager/campaigns/${tree.campaign.id}/review`)).data;
      setIssues(r.issues);
      const errs = r.issues.filter((i) => i.id === sel.id && i.level === sel.level && i.severity === "error");
      if (errs.length) { toast.error(`Complete ${errs.length} required item(s) on this step first: ${errs[0].message}`); return; }
      go(order[idx + 1]);
    } catch (e) { toast.error(errMsg(e)); } finally { setMoving(false); }
  };
  const nextLabel = order[idx + 1] ? { campaign: "Campaign", adset: "Ad set", ad: "Ad", review: "Review & publish" }[order[idx + 1].level] : null;
  return (
    <AmCtx.Provider value={ctx}>
      <div className="space-y-4" data-testid="unified-builder">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><div className="text-sm font-semibold">Campaigns</div><div className="text-xs text-slate-500">Campaign → Ad set → Ad. Everything is created on your Meta ad account in paused status after your review.</div></div>
          <NewCampaign opts={opts} projectId={projectId} prefill={prefill} onCreated={(t) => { setTree(t); setSel({ level: "campaign", id: t.campaign.id }); setTab("build"); }} />
        </div>
        <Readiness rd={readiness} />
        <div className="grid lg:grid-cols-[300px_1fr] gap-5">
          <aside className="space-y-3 min-w-0">
            <div className="rounded-xl border border-slate-800 p-2 max-h-64 overflow-y-auto" data-testid="am-campaign-list">
              {list.map((c) => (
                <button key={c.id} onClick={() => open(c.id)} data-testid={`am-campaign-${c.id}`}
                  className={`w-full text-left rounded-md px-2 py-1.5 hover:bg-slate-900 ${tree?.campaign.id === c.id ? "bg-slate-900" : ""}`}>
                  <div className="text-sm truncate">{c.name}</div>
                  <div className="flex items-center gap-2"><StatusPill x={c} /><span className="text-[10px] text-slate-500">{c.adset_count} sets · {c.ad_count} ads · {dt(c.updated_at)}</span></div>
                </button>))}
              {!list.length && <p className="text-xs text-slate-500 p-2">No campaigns yet. Press “Create campaign”.</p>}
            </div>
            {tree && <div className="rounded-xl border border-slate-800 p-2"><Tree onCampaignGone={() => { setTree(null); setSel(null); loadList(); }} onDuplicatedCampaign={(id) => setSel({ level: "campaign", id })} /></div>}
          </aside>
          <main className="min-w-0 rounded-xl border border-slate-800 p-4 scroll-mt-4" data-testid="am-editor">
            {!tree ? <p className="text-sm text-slate-500" data-testid="am-empty">Select a campaign or create a new one.</p> : (
              <Tabs value={tab} onValueChange={setTab}>
                {tab !== "report" && <Steps cur={tab === "review" ? "review" : sel?.level} order={order} idx={idx} go={go} />}
                <TabsList className="mb-4 flex-wrap h-auto"><TabsTrigger value="build" data-testid="am-tab-build">Build</TabsTrigger><TabsTrigger value="review" data-testid="am-tab-review">Review & publish</TabsTrigger>
                  <TabsTrigger value="report" data-testid="am-tab-report">Status & reporting</TabsTrigger></TabsList>
                <TabsContent value="build">
                  {entity && (sel.level === "campaign" ? <CampaignForm key={entity.id} c={entity} /> : sel.level === "adset" ? <AdSetForm key={entity.id} a={entity} /> : <AdForm key={entity.id} ad={entity} />)}
                </TabsContent>
                <TabsContent value="review">{tab === "review" && <ReviewPanel />}</TabsContent>
                <TabsContent value="report">{tab === "report" && <Insights />}</TabsContent>
                {tab !== "report" && idx >= 0 && (
                  <div className="sticky bottom-0 z-20 -mx-4 -mb-4 mt-6 px-4 py-3 border-t border-slate-800 bg-background/95 backdrop-blur flex items-center justify-between gap-2 pr-20 sm:pr-4" data-testid="step-footer">
                    <Button variant="secondary" disabled={idx <= 0} onClick={async () => { await flushRef.current?.(); go(order[idx - 1]); }} data-testid="step-back"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
                    <span className="text-[11px] text-slate-500 hidden sm:inline">Step {idx + 1} of {order.length} · your entries are saved automatically</span>
                    {nextLabel && <Button disabled={moving} onClick={next} data-testid="step-next">Next: {nextLabel}<ArrowRight className="h-4 w-4 ml-1" /></Button>}
                  </div>)}
              </Tabs>)}
          </main>
        </div>
      </div>
    </AmCtx.Provider>
  );
}
